import React, { FC } from 'react';
import { createUseStyles } from 'react-jss';
import colors from '@packages/core/styles/colors';
import { Typography } from '@packages/ui/shared';
import { StyleProp, TextStyle } from 'react-native';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

export interface QuickReferenceCardProps {
    Icon: JSX.Element;
    title: string;
    image: string;
    version: string;
    onClick?: () => void;
    flag?: boolean;
}

const useStyles = createUseStyles({
    cardContainer: {
        border: `1px solid ${colors.grayThree}`,
        borderRadius: '4px',
        width: 265,
        minWidth: 265,
        maxWidth: 265,
        minHeight: 250,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        backgroundColor: '#fff',
        boxShadow: '0px 2px 5px rgba(0, 0, 0, 0.1)',
        cursor: 'pointer',
        transition: 'box-shadow 0.2s, transform 0.2s',
        '&:hover': {
            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.15)',
            transform: 'translateY(-2px)',
        },
    },
    title: {
        display: 'flex',
        gap: 8,
        alignItems: 'center',
        justifyContent: 'start',
        fontSize: '1rem',
        fontWeight: 'bold',
        width: '100%',
        maxWidth: '100%',
        backgroundColor: colors.grayOne,
        padding: '16px 12px',
    },
    text: {
        flex: 1,
        fontSize: 15,
        fontWeight: 400,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    imageContainer: {
        borderBottom: `1px solid ${colors.grayThree}`,
        padding: 16,
    },
    image: {
        height: '100%',
        width: '100%',
        objectFit: 'contain',
    },
    version: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'start',
        height: '100%',
        width: '100%',
        padding: 12,
    },
    customTooltip: {
        fontSize: 14,
        padding: '8px 12px',
        maxWidth: 300,
        textAlign: 'left',
    },
});

export const QuickReferenceCard: FC<QuickReferenceCardProps> = (props) => {
    const classes = useStyles();
    const { Icon, title, image, version, onClick, flag = true } = props;

    const typographyStyle = {
        fontSize: '13px',
        fontWeight: '400',
        lineHeight: '16px',
    } as unknown as StyleProp<TextStyle>;

    return (
        <div className={classes.cardContainer} onClick={onClick}>
            <div className={classes.title}>
                {flag && Icon}
                <OverlayTrigger
                    placement="top"
                    delay={{ show: 250, hide: 400 }}
                    overlay={
                        <Tooltip id={`tooltip-${title}`} className={classes.customTooltip}>
                            {title}
                        </Tooltip>
                    }
                >
                    <span className={classes.text}>{title}</span>
                </OverlayTrigger>
            </div>
            <div className={classes.imageContainer}>
                <img className={classes.image} src={image} alt={`Image for ${title}`} />
            </div>
            <div className={classes.version}>
                <Typography style={typographyStyle}>Version: {version}</Typography>
            </div>
        </div>
    );
};
